import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  inject,
  output,
  input,
  viewChildren,
  AfterViewInit,
} from '@angular/core';
import { IdentifiedAssistEvent, ResourceAttendee } from '@assist/shared/data';
import { translation } from '@cue/translations';
import { LoadingComponent } from '@cue/loading';
import { ResponsiveService } from '@cue/responsivity';
import { TranslocoPipe } from '@jsverse/transloco';
import { ReservationCardComponent } from '../reservation-card/reservation-card.component';
import { cueCaretLeftFill, cueCaretRightFill, IconComponent } from '@cue/ui/icons';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { RouterLink, RouterLinkActive } from '@angular/router';

@Component({
  selector: 'assist-scheduler',
  templateUrl: './scheduler.component.html',
  styleUrls: ['./scheduler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe, ReservationCardComponent, LoadingComponent, IconComponent, RouterLink, RouterLinkActive],
})
export class SchedulerComponent implements AfterViewInit {
  public responsiveService = inject(ResponsiveService);
  confirmationRequired = true;
  _observer?: IntersectionObserver;
  _observed: any = [];
  translation = translation;

  ICONS = {
    cueCaretLeftFill,
    cueCaretRightFill,
  };
  refresh = output<any>();
  detail = output<IdentifiedAssistEvent>();
  confirm = output<IdentifiedAssistEvent>();
  change = output<IdentifiedAssistEvent>();
  show = output<{
    event: IdentifiedAssistEvent;
    resourceAttendee: ResourceAttendee;
  }>();
  delete = output<IdentifiedAssistEvent>();
  deleteSeries = output<IdentifiedAssistEvent>();
  navigate = output<{
    event: IdentifiedAssistEvent;
    resourceAttendee: ResourceAttendee;
  }>();
  finish = output<IdentifiedAssistEvent>();
  cancelParticipation = output<IdentifiedAssistEvent>();

  events = input<IdentifiedAssistEvent[]>([]);
  loading = input<boolean>(false);
  isColleague = input<boolean>(false);
  view = viewChildren<ElementRef<HTMLDivElement>>('view');

  cards = viewChildren(ReservationCardComponent, { read: ElementRef });

  setupObserver() {
    if (this._observer) {
      this._observed.forEach((t) => this._observer.unobserve(t));
      this._observer?.disconnect();
    }

    if (this.view()[0]) {
      const OPTIONS: IntersectionObserverInit = {
        root: this.view()[0].nativeElement,
        rootMargin: '-5% 0px 0px 30%',
        threshold: 0.1,
      };
      const handleIntersection = (entries, observer) => {
        for (const entry of entries) {
          entry.target.style.setProperty('--show', entry.isIntersecting ? 1.0 : 0.85);
          entry.target.style.setProperty(
            '--shadow',
            entry.isIntersecting ? '0px 8px 36px rgb(100 40 0 / 10%)' : '0px 4px 30px rgb(0 0 0 / 1%)'
          );
        }
      };
      this._observer = new IntersectionObserver(handleIntersection, OPTIONS);
      this.cards()
        .map((x) => x.nativeElement)
        .forEach((tile) => {
          this._observed.push(tile);
          this._observer.observe(tile);
        });
    }
  }

  setupObserverEffect = explicitEffect([this.cards, this.view], () => {
    this.setupObserver();
  });

  ngAfterViewInit() {
    this.setupObserver();
  }

  getSkeletonCount(heights: number) {
    return Math.floor((heights - 100) / 50);
  }
}
